import React, { useEffect, useState } from "react";
import { Download, MonitorSmartphone, X } from "lucide-react";
import { canInstallNow, initInstallPrompt, installApp, isIos, isStandalone, onInstallStateChange } from "@/lib/pwa";
import { useToast } from "@/lib/toast";

const SNOOZE_KEY = "assignoo.install.snoozed-until";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

/** Global install affordance: native prompt banner, iOS hint, and shareable trigger for Profile. */
export function InstallPrompt() {
  const toast = useToast();
  const [, force] = useState(0);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    initInstallPrompt();
    return onInstallStateChange(() => force((v) => v + 1));
  }, []);

  // Auto-suggest once (Android/desktop) — snoozed for 3 days after dismiss.
  useEffect(() => {
    if (isStandalone()) return;
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < until) return;
    const t = setTimeout(() => { if (canInstallNow()) setShow(true); }, 2500);
    return () => clearTimeout(t);
  });

  const install = async () => {
    const r = await installApp();
    setShow(false);
    if (r === "ios") setIosHelp(true);
    else if (r === "installed") toast.show("Assignoo is already installed", "info");
    else if (r === "unavailable") toast.show("Install isn't available in this browser. On iPhone: Share → Add to Home Screen.", "info");
  };
  const dismiss = () => { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); setShow(false); };

  return (
    <>
      {show && !isStandalone() ? (
        <div className="fade-in fixed inset-x-0 bottom-[72px] z-[7000] flex justify-center px-4 md:bottom-5" data-testid="install-banner">
          <div className="sheet-in flex w-full max-w-lg items-center gap-3 rounded-2xl border border-a-border bg-a-surface p-3 shadow-[0_10px_30px_rgba(15,23,42,0.18)]">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-a-border bg-white"><img src="/branding/icon-192.png" alt="Assignoo" className="h-9 w-9" /></span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-extrabold text-a-text">Install Assignoo</p>
              <p className="truncate text-xs text-a-muted">Add it to your home screen — opens like a native app.</p>
            </div>
            <button onClick={install} data-testid="install-cta" className="btn-press flex items-center gap-1.5 rounded-xl bg-a-primary px-3.5 py-2.5 text-[13px] font-extrabold text-white hover:brightness-110"><Download size={15} />Install</button>
            <button onClick={dismiss} className="text-a-muted hover:text-a-text" data-testid="install-dismiss"><X size={18} /></button>
          </div>
        </div>
      ) : null}
      {iosHelp ? (
        <div className="fade-in fixed inset-0 z-[8000] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setIosHelp(false)}>
          <div className="sheet-in w-full max-w-md rounded-t-[22px] border-t border-a-border bg-a-surface p-5 pb-8 sm:rounded-[22px] sm:border" onClick={(e) => e.stopPropagation()} data-testid="install-ios-sheet">
            <div className="mb-2 flex items-center gap-2.5"><MonitorSmartphone size={20} className="text-a-primary" /><p className="text-[17px] font-extrabold text-a-text">Install on this iPhone</p></div>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-6 text-a-text">
              <li>Tap the <b>Share</b> button in Safari (the square with an arrow).</li>
              <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
              <li>Tap <b>Add</b> — Assignoo will open like a native app.</li>
            </ol>
            <button onClick={() => setIosHelp(false)} className="btn-press mt-4 w-full rounded-xl bg-a-primary py-3 font-extrabold text-white" data-testid="install-ios-ok">Got it</button>
          </div>
        </div>
      ) : null}
    </>
  );
}
